import TemplateView from "../../../TemplateView";
import TasksColumn from "./TasksColumn";
import messageResource from "../../../messageResource";
import {WorkflowStatusType} from "../../../workflowStatus/constants";
import {isArraysWithIdsItemsOrderEqual} from "../../../atUtils";
import {getStatusClassByType} from '../../../workflowStatus/utils'

export default class ProjectTasksBlock extends TemplateView {
    constructor({
        projectId = null,
        canReopenProject = false,
        canManageAllProjectTasks = false,
        onHeaderClick = () => {},
        onSelectTaskHandler = () => {},
        onTaskClick = () => {},
        onTaskStatusAndPositionChanged = () => {},
        onTaskDragStart = () => {},
        onTaskDragStop = () => {},
        onTasksColumnDragOver = () => {},
        onTasksColumnDragOut = () => {},
        onCardActiveSelectionDisappear = () => {},
    }) {
        super()

        this.projectId = projectId;

        this.canReopenProject = canReopenProject;
        this.canManageAllProjectTasks = canManageAllProjectTasks
        this.taskColumnsMap = {};

        this.callbacks = {
            onHeaderClick,
            onSelectTaskHandler,
            onTaskClick,
            onTaskStatusAndPositionChanged,
            onTaskDragStart,
            onTaskDragStop,
            onTasksColumnDragOver,
            onTasksColumnDragOut,
            onCardActiveSelectionDisappear,
        }

        this.state = {
            statuses: [],
            collapsed: false,
            tasksByStatus: {},
            taskIdWithDetailsOpen: null,
            selectedTaskIds: {},
            lastCreatedTaskIds: {},
            recentlyCreatedTaskIds: {},
            draggingTaskStatusId: null,
            currentPrecedingTaskId: null,
            canDropDraggingTask: false,
            updatingTaskIds: {},
            minimizedCards: false,
            showSelection: false,
            showSingleProject: false,
            showSingleCustomer: false,
            showEstimate: false,
            ignoreProjectCollapse: false,

            needRefreshSortable: false,

            customerName: '',
            projectName: '',
            projectArchived: false,
            customerArchived: false,
        }

        this.onAnyStatePropertyChanged(["statuses", "tasksByStatus"], (state, prevState) => {
            if (!isArraysWithIdsItemsOrderEqual(state.statuses, prevState.statuses)) {
                this.$taskColumns.empty();
                this.taskColumnsMap = {}
            }

            this._updateTasks()
        })

        this.onAnyStatePropertyChanged(["showSingleCustomer", "showSingleProject"], this._updateNameShowing)
        this.onAnyStatePropertyChanged(["collapsed", "showSingleProject"], this._updateCollapsed)
        this.onAnyStatePropertyChanged(["minimizedCards", "showEstimate"], ({minimizedCards, showEstimate}) =>
            this._updateTasksView(minimizedCards, showEstimate))

        this.onAnyStatePropertyChanged(["selectedTaskIds", "lastCreatedTaskIds"], () => this._updateTasks())
        this.onStatePropertyChanged("taskIdWithDetailsOpen", taskId => this._updateTasksIdWithDetailsOpen(taskId))
        this.onAnyStatePropertyChanged(["canDropDraggingTask", "projectArchived"], () => this._updateTaskColumnsCanDrop())
        this.onStatePropertyChanged("updatingTaskIds", updatingTaskIds => this._updateTaskColumnsUpdatingTaskIds(updatingTaskIds))

        this.onStatePropertyBecameTrue("needRefreshSortable", needRefreshSortable => {
            if (this.state.draggingTaskStatusId) this.taskColumnsMap[this.state.draggingTaskStatusId].setState({needRefreshSortable})
            this.setStateInternal({needRefreshSortable: false})
        })

        this.onStatePropertyChanged('ignoreProjectCollapse', (ignoreProjectCollapse) => {
            this.$icon.toggleClass('hide', ignoreProjectCollapse)
        })

        this.onStatePropertyChanged('projectArchived', (projectArchived) => {
            this.$projectArchivedLabel.toggle(projectArchived)
        })

        this.onStatePropertyChanged('customerArchived', (customerArchived) => {
            this.$customerArchivedLabel.toggle(customerArchived)
        })

        this.onStatePropertyChanged('customerName', (customerName) => {
            this.$customerName.text(customerName)
        })

        this.onStatePropertyChanged('projectName', (projectName) => {
            this.$projectName.text(projectName)
        })
    }

    templateHtml() {
        return "<div class='projectTasksBlock'>" +
               "  <div class='header'>" +
               "    <div class='icon'></div>" +
               "    <div class='customerNamePlace'>" +
               "        <span class='customerName'></span>" +
               "        <span class='customerArchivedLabel'><%=archivedLabel%></span>" +
               "        <span class='separator'></span>" +
               "    </div>" +
               "    <div class='projectName'></div>" +
               "    <div class='projectArchivedLabel'><%=archivedLabel%></div>" +
               "    <div class='line'></div>" +
               "  </div>" +
               "  <div class='taskColumns'></div>" +
               "</div>"
    }

    messageKeys() {
        return {
            archivedLabel: "common.archived"
        }
    }

    afterRender() {
        this.$header = this.find(".header")
        this.$icon = this.find('.icon')
        this.$header.click(() => this.callbacks.onHeaderClick())
        this.$customerNamePlace = this.find('.customerNamePlace')
        this.$taskColumns = this.find(".taskColumns")
        this.$projectArchivedLabel = this.find('.projectArchivedLabel')
        this.$customerArchivedLabel = this.find('.customerArchivedLabel')
        this.$customerName = this.find('.customerName')
        this.$projectName = this.find('.projectName')
    }

    _updateCollapsed = () => {
        this.getEl().toggleClass("collapsed", !this.state.showSingleProject && this.state.collapsed)
    }

    _updateNameShowing = () => {
        if (!this.state.showSingleProject) {
            this.$header.show()
            if (!this.state.showSingleCustomer) {
                this.$customerNamePlace.show()
            } else {
                this.$customerNamePlace.hide()
            }
        } else {
            this.$header.hide()
        }
    }

    _updateTasks() {
        this.state.statuses.forEach(status => {
            if (!this.taskColumnsMap[status.id]) {
                this.taskColumnsMap[status.id] = new TasksColumn({
                    colorId: status.colorId,
                    props: {
                        projectId: this.projectId,
                        workflowStatus: status,
                        emptyColumnText: messageResource.getMessage("task.list.kanban.no_tasks_with_status"),
                        canDrop: !this.state.projectArchived || status.type === WorkflowStatusType.COMPLETED || this.canReopenProject,
                    },
                    onTaskClick: taskId => this.callbacks.onTaskClick(taskId),
                    onTaskSelectChanged: this.callbacks.onSelectTaskHandler,
                    onTaskDragStart: ({id, currentPrecedingId}) => {
                        this.setStateInternal({
                            draggingTaskStatusId: status.id,
                            currentPrecedingTaskId: currentPrecedingId
                        })
                        this.callbacks.onTaskDragStart(this.projectId, id)
                    },
                    onTaskDragStop: () => {
                        this.setStateInternal({
                            draggingTaskStatusId: null,
                            currentPrecedingTaskId: null
                        })
                        this.callbacks.onTaskDragStop()
                    },
                    onTaskDragOver: this.callbacks.onTasksColumnDragOver,
                    onTaskDragOut: () => {
                        this.callbacks.onTasksColumnDragOut()
                    },
                    onTaskAdded: ({workflowStatusId, precedingTaskId}) => {
                        this.callbacks.onTaskStatusAndPositionChanged({
                            projectId: this.projectId,
                            workflowStatusId: workflowStatusId,
                            precedingTaskId: precedingTaskId,
                            currentPrecedingTaskId: this.state.currentPrecedingTaskId
                        })
                    },
                    onTaskPositionChanged: ({taskId, precedingTaskId, workflowStatusId}) => {
                        this.callbacks.onTaskStatusAndPositionChanged({
                            taskId: taskId,
                            projectId: this.projectId,
                            workflowStatusId: workflowStatusId,
                            precedingTaskId: precedingTaskId,
                            currentPrecedingTaskId: this.state.currentPrecedingTaskId
                        })
                    },
                    onCardActiveSelectionDisappear: this.callbacks.onCardActiveSelectionDisappear,
                }).init(this.$taskColumns)
            }

            const tasks = this.state.tasksByStatus[status.id] || []

            this.taskColumnsMap[status.id].setState({
                tasks,
                selectedTaskIds: this.state.selectedTaskIds,
                taskIdWithDetailsOpen: this.state.taskIdWithDetailsOpen,
                lastCreatedTaskIds: this.state.lastCreatedTaskIds,
                recentlyCreatedTaskIds: this.state.recentlyCreatedTaskIds,
                minimizedCards: this.state.minimizedCards,
                showSelection: this.state.showSelection,
                showEstimate: this.state.showEstimate,
                canDrop: this.state.canDropDraggingTask && (!this.state.projectArchived || status.type === WorkflowStatusType.COMPLETED || this.canReopenProject)
            });
        })

        const openStatusClass = getStatusClassByType(WorkflowStatusType.OPEN)
        const lastOpenStatusColumn = this.$taskColumns.find('.' + openStatusClass).last()
        lastOpenStatusColumn.addClass("lastOpen")
    }

    _updateTasksIdWithDetailsOpen(taskId) {
        Object.values(this.taskColumnsMap).forEach(taskColumn => taskColumn.setState({
            taskIdWithDetailsOpen: taskId
        }))
    }

    _updateTasksView(minimizedCards, showEstimate) {
        this.state.statuses.forEach(status => {
            this.taskColumnsMap[status.id].setState({
                minimizedCards,
                showEstimate,
            });
        })
    }

    _updateTaskColumnsCanDrop() {
        Object.values(this.taskColumnsMap).forEach(taskColumn => {
            taskColumn.setState({
                canDrop: this.state.canDropDraggingTask && (!this.state.projectArchived || taskColumn.props.workflowStatus.type === WorkflowStatusType.COMPLETED || this.canReopenProject)
            })
        })
    }

    _updateTaskColumnsUpdatingTaskIds(updatingTaskIds) {
        Object.values(this.taskColumnsMap).forEach(taskColumn => {
            taskColumn.setState({updatingTaskIds})
        })
    }
}


// WEBPACK FOOTER //
// ./web/js/taskManagement/kanban/components/ProjectTasksBlock.js